package org.multiplatform.kotfolio.pages.compose.internal

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/internal/lifecycle")
@Composable
fun ComposeLifeCyclePage() {
    BlogLayout(
        PageProperties(
            "Lifecycle Composition",
            "Jetpack Compose doesn't follow the traditional Android lifecycle. It's driven by state and only the affected parts of the Ul are updated, making Compose efficient, reactive, and declarative."
        ),
        BlogProperties("Jetpack Compose", "Lifecycle of composable functions or Composition")
    ) {

        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "The Composition Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em, bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "(Enter the composition, Recompose 0 or more times, and Leave the composition).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Android Host Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em, bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "(STARTED, RESUMED, STOPPED, etc.).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lifecycle of composable functions or Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em, bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Composable enters the Composition when it appears in the tree, recomposes when its inputs change, and leaves the Composition when it is no longer present in the tree. Effects like LaunchedEffect and DisposableEffect tie their lifetime to this lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions do not have a traditional lifecycle like Android Views or Activities. It's driven by state and only the affected parts of the Ul are updated, making Compose efficient, reactive, and declarative.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.COMPOSABLE_LIFECYCLE_URL,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Composable Lifecycle"
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose, being a declarative UI framework, approaches UI construction differently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of manually managing a tree of Views, you describe the UI in terms of Composables — functions that define the UI and its behavior in a declarative way.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose handles the rendering, updating, and disposal of these Composables efficiently based on state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Jetpack Compose UI toolkit manages the lifecycle of the UI components in a way that is similar to the traditional Android lifecycle, but with some differences.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The Jetpack Compose lifecycle consists of three main phases:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition (created)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition (updated)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Disposal (destroyed)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Initial Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When Jetpack Compose runs your composables for the first time, during “initial composition”, it will keep track of the composables that you call to describe your UI in a Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The function creates the initial UI elements based on the given state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Any side-effects like LaunchedEffect or remember are initialized and remembered for future recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The UI hierarchy is built and added to the composition tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Composition - (Initial build of the Ul tree)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Composition is a tree-structure of the composables that describe your UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial composition is when a Composable tree gets rendered for the first time. (Composition is the initial phase where the UI elements are created for the first time)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a UI component is first created, it enters the composition phase.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Points - (1. Trigger - Initial render, 2. Frequency - Once, 3. Cost - Higher (builds tree))",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "initial Rendering: When a composable function is first called, Compose evaluates it and constructs the UI hierarchy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State Initialization: Initial state and side effects are set up during this phase.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When Jetpack Compose runs your composables for the first time, during initial composition, it will keep track of the composables that you call to describe your UI in a Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This phase only occurs once for a given part of the UI (unless it needs to be recreated). Once composed, the UI stays on the screen until it is either updated (via recomposition) or removed (via disposal).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a composable function is called for the first time, Jetpack Compose: builds the UI tree by evaluating the composable functions and creating UI elements and adds the resulting UI elements to the screen, establishing the initial view structure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this phase, the Compose runtime calls the Composable function that defines the UI component. The Composable function describes the structure and behavior of the UI component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "What Happens",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The UI tree is created.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose observes the state used in the composable functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI Setup",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Layouts and visual elements are created and arranged.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                            @Composable
                            fun Greeting(name: String) {
                                Text(text = name!)
                            }
                            // When Greeting is first invoked, it triggers the composition phase, rendering name!".
                        """.trimIndent()
                )
            }

            SpanText(
                text = "Composition Tree",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All of your composable functions come together to form the Composition Tree, a structural representation of your UI. Think of it as a map, where each composable is a node, and relationships between composables are edges. The Composition Tree allows Compose to track which parts of the UI need to be updated when the state changes, optimizing rendering and recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When state changes, Compose checks the slot table to match what existed with what should exist now, updates only what’s needed, and then Skia paints the pixels. Let’s understand each in detail.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition Tree: It’s just Compose’s way of keeping track of your Composables so it knows what to poke when state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Nodes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Each element in your UI, created by a composable function, is represented as a node(composables) in the tree.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Parent-Child Relationships",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Nodes can have parent-child relationships, reflecting the nesting of composables within each other. For example, a Column composable might contain several Text composables as its children.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Root",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The top-level composable function in your UI acts as the root of the tree.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dynamic Structure",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The composition tree is dynamic. It can change as your app’s state changes, with nodes being added, removed, or modified.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Recomposition - (Re-executes only the (Trigger) invalidated parts / nodes)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Then, when the state of your app changes, Jetpack Compose schedules a recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The UI component can be recomposed whenever there is a change in the data that it depends on. (The composable function is recomposed whenever a new parameters are being input. This phenomena is called Recomposition.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the recomposition phase, the Compose runtime checks whether the UI component needs to be updated, and if so, it calls the Composable function again. The Composable function can update the UI component by modifying its properties or structure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack compose does smart recomposition, which means it skips the composition if the input hasn’t changed unlike invalidate() which redraws everything even if it’s not changed. The compose Framework intelligently recomposes only the components that changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition can execute the composable functions in any order. In the below composable, there’s no guarantee that FeedHeader executes first.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use lambda-based modifier whenever possible, this will skip the unnecessary phase of composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use key for lazy list: key help composition to identify whether to regenerate the item or recompose it whenever required. By default, each item’s position is the key but this might lead to unexpected behavior when the position of items changes so to avoid this use a unique and stable Id as an item key.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lambda functions are treated as unstable. Use reusable lambda or method references to avoid recreation on every recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        //Bad Way
                        @Composable
                        fun NameList(viewModel: MyVM) {
                            val names by viewModel.names.collectAsState()
                            names.forEach { name ->
                                Text(name, modifier = Modifier.clickable { viewModel.onNameClicked() })
                            }
                        }

                        //Good Way
                        @Composable
                        fun NameList(viewModel: MyVM) {
                            val names by viewModel.names.collectAsState()
                            val onClick = remember(viewModel) { { viewModel.onNameClicked() } }
                                names.forEach { name ->
                                NameItem(name, onClick)
                            }
                        }
                        """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose check the stability on input to favour smart recomposition. Use @stable annotation to tell the compiler that your class is stable. Stable annotation indicates that the function will return the same result if the same parameters are passed in. But the parameters and results should be Stable, Immutable, or primitive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use derivedStateOf to avoid unnecessary recomposition. With the derivedStateOf() , result of a calculation will be cached, so the result.value will never invoke the calculation again. Also, derivedStateOf emit the new value only if specified conditions meet.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Composition can only be produced by an initial composition and updated by recomposition. The only way to modify a Composition is through recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "What Happens",
                    modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose reevaluates the parts of the UI tree affected by state changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It ensures only the necessary changes are applied, minimizing overhead.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "Triggers",
                    modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State changes (mutableStateOf, StateFlow, LiveData, etc.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Re-entering the composition with new parameters.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Updating a parent composable that causes all children to recompose.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Using unstable data types or non-remembered lambdas.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "How does recomposition actually work?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every composable is a function that can re-execute when state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose tracks state reads.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the state changes, Compose re-executes the affected composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Not everything recomposes; only the nodes depending on changed state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Recomposition Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition is the process by which Jetpack Compose updates parts of the UI when there is a change in state. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a state variable (like a MutableState) changes, Jetpack Compose identifies the composables that depend on that state and re-runs only those composables, updating the UI accordingly. This minimizes the work needed to keep the UI in sync with the underlying data, which improves performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions in Jetpack Compose respond to state changes. When the state a composable depends on changes, the function is “recomposed,” meaning it is re-executed to update the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is similar to how reactive programming works — UI elements update automatically based on the data they depend on.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = $$"""
                        import androidx.compose.runtime.*
                        import androidx.compose.material.*

                        @Composable
                        fun CounterScreen() {
                            var count by remember { mutableStateOf(0) }

                            Column {
                                Text("Counter: $count")
                                Button(onClick = { count++ }) {
                                    Text("Increment")
                                }
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "count is a piece of state initialized with 0.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember and mutableStateOf make count a state variable, meaning any change to count will trigger recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every time the button is clicked, count is incremented, which triggers a recomposition of the CounterScreen composable to display the updated value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "1. Triggers Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A recomposition is triggered when there’s a change in the state of a composable. The key here is that recomposition isn’t always a bad thing — Compose is designed to be as efficient as possible by recomposing only the necessary parts. This could be anything from:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State Changes: When a piece of state that a composable depends on is modified.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User Interactions: Events like button clicks or text input that alter state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Configuration Changes: Rotating the device or changing themes can trigger recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "2. Snapshot State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose uses Snapshot State to manage recomposition efficiently. A snapshot is a point-in-time snapshot of the UI’s state, allowing Compose to determine what has changed and react accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is critical because Compose ensures that during recomposition, any state reads are consistent with the state of the app when recomposition started. This means that even if multiple state changes occur in rapid succession, Compose can avoid glitches or inconsistencies in the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "3. Recomposition Cycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the state of a composable changes, Compose enters the recomposition cycle:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The state of the affected composable is checked.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Composition Tree is reevaluated to determine which parts of the UI depend on the changed state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Only the affected parts are recomposed, avoiding unnecessary updates to the entire tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Compose Uses a Directed Acyclic Graph (DAG) for Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Many developers know that the Composition Tree represents the structure of the UI, but few realize that this tree is actually modeled as a Directed Acyclic Graph (DAG). ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This means that composables in Jetpack Compose are arranged in a way that no composable ever directly depends on itself or creates circular dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The DAG structure ensures that each composable has a clear relationship, making it easier for Compose to track which parts of the UI need to be recomposed when the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This design also prevents infinite recompositions and makes scaling your UI more predictable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Recomposition Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial composition: When a composable function is called for the first time, it’s composed from scratch. Compose runs all composable functions once. It builds a “composition tree” of UI elements and remembers which composables read which pieces of state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Change Detection: When a MutableState changes, Compose marks all composables that read that state as invalid (dirty).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition: If any input (like state) that a composable reads changes, the framework schedules a recomposition. The parts of the UI that depend on the changed data are recomposed, while other parts remain intact. Compose schedules a recomposition (not immediately, but efficiently batched).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Selective Re-execution: Only invalidated composables are re-executed. The rest of the UI tree remains unchanged.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Skipping: If during recomposition Compose finds that nothing has changed in the dependencies of a particular composable, it skips recomposing that composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI Updated: Compose applies the minimal required changes to the UI (diff-based updates).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Stability and Skip ability",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose track changes in state and skip recompositions for stable and unchanged states, optimizing performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Strong Skipping & Lambda Memoization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@DontMemoize tells the Compose compiler not to cache a parameter for recomposition skipping, useful when a value appears stable but changes internally.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When strong skipping is enabled, the compiler automatically memoizes every lambda within a composable using remember(captures…). Captures become keys; if they haven't changed, the same lambda reference is preserved. This significantly reduces skip breaks caused by “new lambdas on every recomposition” in callback-heavy UIs (e.g., onClick/onValueChange).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key comparison is “stability-aware”: unstable keys are compared using ===, while stable keys are compared using equals(). In other words, automatic memoization aligns with the “composable parameter comparison” logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Critical: It is not “unstable capture → lambda unstable.” Lambda is considered stable; the problem is that when strong skipping is disabled, lambda is reallocated at every recomposition, causing parameter equality to be lost (the reference changes). In some cases, you may want lambda to be “fresh” each time (such as for tracing/instrumentation).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A common misconception is that lambdas with unstable captures are themselves unstable objects. This is not true. The Compose compiler always considers lambdas to be stable. However, with Strong skipping disabled, the compiler does not memoize them and the runtime allocates them during recomposition. As such, lambdas with unstable captures cause Compose not to skip composables because they have unequal parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //onClick
                    @Composable
                    fun DebuggableButton(
                        onClick: () -> Unit,
                    ) {
                        val click = @DontMemoize {
                           onClick()
                        }
                    }
                    
                    @Composable
                    fun MyText(
                        @DontMemoize textProvider: () -> String
                    ) {
                        Text(textProvider())
                    }
                    
                    //Function / Lambda parameters
                    @Composable
                    fun Screen(
                        @DontMemoize onEvent: (UiEvent) -> Unit
                    ) { }
                    
                    //Mutable or externally changing objects
                    @Composable
                    fun CameraPreview(
                        @DontMemoize controller: CameraController
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Snapshot Management / System - (Compose tracks state reads)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Read: When a Composable reads a State object, the Snapshot system notifies the current $composer.

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the context of SnapshotStateList, \"snapshot\" refers to a mechanism for capturing a static, immutable version of the list's state at a specific point in time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose introduces the Snapshot System to maintain UI consistency and manage state changes efficiently. Without it, handling multiple state changes in an app could lead to race conditions, inconsistencies, or performance bottlenecks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A snapshot is a record of the state at a particular moment. Compose uses this concept to track state changes in a controlled manner. When you read state inside a composable, Compose captures the state from that exact moment in time, ensuring that any updates to the UI are predictable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotStateList is a special type of mutable list that integrates with Jetpack Compose's state observation system. When the list's contents change, Compose recomposes any composable functions that depend on it, updating the UI accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observability: State is read when composables access it during recomposition, and written when state changes (e.g., when you modify a mutableStateOf value). The snapshot system ensures that state updates are batched and applied all at once, avoiding UI flickers or partial updates. Write operations are enclosed in transactions that bundle state changes together, allowing Compose to apply them atomically when the transaction is committed. This reduces overhead and ensures a smooth user experience.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshotting: When a change is made to a SnapshotStateList, a new snapshot is created instead of modifying the original list directly. This snapshot is a separate, immutable collection that represents the list's state at a particular moment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s snapshot system also ensures thread safety when managing state. All updates are performed on a single thread (the main UI thread) to avoid concurrency issues. While Compose supports asynchronous tasks, state changes are carefully synchronized to prevent race conditions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Points",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State Tracking: Compose uses snapshots to manage and track changes in state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Atomic Updates: State changes are batched and committed in a snapshot, ensuring atomic updates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Snapshot Observers: Observers are notified of changes when a snapshot is committed, triggering recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                            @Composable
                            fun Timer() {
                                val currentTime = remember { mutableStateOf(LocalTime.now()) }

                                LaunchedEffect(Unit) {
                                    while (true) {
                                        delay(1000)
                                        currentTime.value = LocalTime.now() // Updates are tracked by the snapshot
                                    }
                                }

                                Text(text = currentTime.value)
                            }
                            // Here, currentTime is tracked within a snapshot, which helps manage updates and recompositions.
                        """.trimIndent()
                )

                SpanText(
                    text = "What Happens",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose tracks state changes using snapshots.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "These snapshots ensure a consistent view of state at any given time.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                    var username by remember { mutableState0f("Guest") }

                    Button (onClick = {username = "Admin"}) {
                        Text ("Login as Admin")
                    }

                    Text ("Welcome, $username!")
                    """.trimIndent()
            )

            SpanText(
                text = "Snapshotting Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial State: When you create a SnapshotStateList using mutableStateListOf, a snapshot is created to represent the initial empty list.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifications: When you add, remove, or modify elements in the list, a new snapshot is generated that reflects the updated state. The previous snapshot remains intact, preserving the previous state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition: Composable functions that use the SnapshotStateList internally hold a reference to a snapshot. When a new snapshot is created, Compose compares it to the currently held snapshot and recomposes only if changes are detected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Subscription",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Composer then registers the current Recompose Scope (the code block being executed) as a “listener” for that specific state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Invalidation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flag parts of UI that need update. When (Trigger) state changes, the affected parts are marked dirty.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Definition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Invalidation is the phase where certain parts of the UI are marked as needing an update. This happens when dependencies of composables change, indicating that recomposition is necessary.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Points",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Marking for Recomposition: When the state or data that a composable depends on changes, the affected parts of the UI are marked for recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Efficient Updates: Compose uses invalidation to determine which parts of the UI need to be updated without recomposing the entire UI tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                            @Composable
                            fun UserProfile(userId: String) {
                                val user = getUser(userId) // Imagine getUser fetches user details based on userId

                                Text(text = user.name)
                            }
                            // If userId changes, the UserProfile composable is invalidated and marked for recomposition to fetch and display new user details.
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Commit",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Definition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Commit is the phase where changes in the snapshot are applied to the UI. After state changes are batched and tracked, they are committed to update the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Points",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Apply Changes: Changes in the snapshot are applied to the UI in a commit phase, ensuring the UI reflects the latest state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition Trigger: Committing changes can trigger recomposition to apply updates to the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In the previous Timer example, after currentTime.value changes, the snapshot commits those changes, which then triggers recomposition of the Text composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Disposal - (Cleans up when the composable leaves the Ul tree)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a UI component is no longer needed, it enters the disposal phase. In this phase, the Compose runtime removes the UI component from the UI hierarchy and calls any cleanup logic that was registered for the component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This typically happens when a composable: goes out of scope due to a change in the UI tree (e.g., navigating away from a screen or conditionally removing a component) or is replaced by another composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Any resources tied to the composable are cleaned up automatically.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Side-effects like DisposableEffect are triggered to release resources",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Developers can use APIs like DisposableEffect to clean up resources when this happens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    DisposableEffect(Unit) { 
                        onDispose { 
                            println("Leaving composition")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Closer Look at the Lifecycle Steps",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Initial Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a composable is first added to the hierarchy, Compose executes the function and builds the UI tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At this stage, remember blocks are evaluated to store state across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State Observation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose observes the state values that the composable accesses.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If these observed states change, the composable becomes invalidated and is scheduled for recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition is triggered when the state changes. Compose re-executes the affected composable functions but reuses parts of the tree that haven’t changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This process ensures that only the necessary parts of the UI are updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Points (1. Trigger - State change, 2. Frequency - Many times (optimized), 3. Cost - Lower (updates only changed parts))",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Efficient Updates: Compose intelligently determines which parts of the UI need updating based on changes in state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Triggering: Recomposition can be triggered by changes in state variables, remember values, or other observed properties.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                            @Composable
                            fun Counter() {
                                var count by remember { mutableStateOf(0) }

                                Column {
                                    Text(text = count)
                                    Button(onClick = { count++ }) {
                                        Text("Increment")
                                    }
                                }
                            }
                            // When the count state changes, Counter undergoes recomposition to update the displayed count.
                        """.trimIndent()
                )
            }

            SpanText(
                text = "Skippability Check",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Before re-executing a composable, Compose checks if it can skip the recomposition. This is based on the stability of the function’s inputs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Resource Cleanup",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a composable is no longer needed, Compose removes it from the hierarchy and cleans up any associated resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Composable Function Works Internally",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compilation and Annotation Processing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Transformations",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The Compose compiler transforms your @Composable functions into code that can be managed within the Compose runtime. This involves converting the functions into a special form that can handle recomposition, state management, and other Compose-specific behaviors.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Code Generation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The compiler generates additional code needed to manage the lifecycle of the composable functions, such as managing how and when they should be re-executed (recomposed) in response to state changes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition Context",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The execution of composable functions occurs within a composition context. This context manages the hierarchy of composables, keeps track of their state, and handles recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When a composable function is called, it is added to a composition tree, a data structure that represents the current UI hierarchy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition is a process where the Compose runtime re-executes composable functions to update the UI based on changes in state or data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When a piece of state that a composable depends on changes, Compose intelligently decides which parts of the UI need to be recomposed. This is key to Compose’s efficiency, as it minimizes unnecessary work by only updating the parts of the UI that need to change.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Management",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State management in Compose is often handled using remember, mutableStateOf, and other state-related functions. The remember function, for example, allows a composable to maintain state across recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State variables are linked to the composition lifecycle. When a state variable changes, it triggers recomposition for any composable functions that depend on it.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Skipping and Reusing Composables",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Compose runtime is optimized to skip recomposition of composables when possible. If a composable function’s inputs (parameters and state) have not changed since the last composition, Compose can skip re-executing that function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This optimization reduces unnecessary work, leading to more efficient UI updates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable Functions and Side Effects",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable functions should ideally be side-effect-free, meaning they should only describe the UI without performing any operations that affect the outside world (e.g., modifying shared data or interacting with hardware).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "To handle side effects (like launching coroutines, interacting with databases, etc.), Compose provides specific mechanisms like LaunchedEffect, SideEffect, and DisposableEffect, which are scoped to the lifecycle of the composable they are used in.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Responsibility Principle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable functions are encouraged to be small and focused, following the single responsibility principle. This makes them easier to understand, test, and reuse.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What is recomposition and how does Compose decide what to recompose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition is re-executing a composable function when its state changes. Compose uses a Positional Memoization system — it tracks which state objects each composable reads during composition. When a state changes, only composables that READ that state are scheduled for recomposition. Compose can skip a composable if all its parameters are stable and unchanged.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Positional memoization, closely tied to this memory mechanism, necessitates an understanding of the data structures employed in Compose. The slot table, a data structure akin to a gap buffer, plays a pivotal role. Representing a list as a flat array in memory, the slot table contains a current index and an empty space known as the gap. Throughout the execution of composable functions, this data structure can be harnessed to store and manipulate data effectively. By recomposing a function, the current index can be reset, enabling selective data skipping, updates, or insertions. Notably, array operations such as get, insert, and remove are constant-time operations, except for moving the gap, which occurs during structural changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Jetpack Compose and View Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Traditional View Lifecycle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Relies on callbacks (onCreate , etc.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Views are explicitly inflated.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lifecycle must be manually managed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose Lifecycle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State-driven, with recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ul tree is declaratively defined.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Resources are managed automatically.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Multiple ways to manage Android lifecycle in Jetpack Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "lifecycle-aware composables",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack Compose provides a number of lifecycle-aware composables, such as LaunchedEffect and DisposableEffect.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "These composables allow you to perform side effects in a lifecycle-aware way, meaning that the side effects will be cleaned up when the composable is no longer needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LocalLifecycleOwner composable",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The LocalLifecycleOwner composable provides you with access to the lifecycle owner of the current composition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can use this lifecycle owner to observe lifecycle events and perform actions accordingly.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel in Jetpack Compose is used for managing and preserving UI-related data across configuration changes. It provides a way to separate the UI logic from the UI components and allows sharing data between multiple Composable functions. ViewModels can be accessed using the viewModel or viewModel<>() functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "While Compose itself is UI-centric, the ViewModel is part of the broader Android architecture (such as Jetpack) and helps manage and persist UI-related data across configuration changes. It can be used in conjunction with Compose to store and manage the state of the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The ViewModel helps in maintaining a clear separation of concerns by separating the UI-related logic from the UI components. It allows developers to keep the business logic and data-related operations in a separate class, promoting a more organized and maintainable codebase.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The ViewModel is lifecycle-aware, meaning it is designed to survive configuration changes like screen rotations or other lifecycle events. It allows the application to retain and manage data even when the UI components are recreated.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose encourages a unidirectional data flow, where UI components observe and display data. The ViewModel serves as a single source of truth for data, and different composable functions can observe and read this shared data, ensuring a consistent UI representation across the app.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "By keeping the business logic in the ViewModel, it becomes easier to unit test the logic without the need for UI components. This separation of concerns enhances testability and allows for more robust testing of the application's functionality.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A ViewModel is a lifecycle-aware component that stores data for your composables. It can be observed by composables, and will automatically update them when the data changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "To use a ViewModel in Compose, you can pass the lifecycleOwner parameter to the ViewModel constructor. The lifecycleOwner will keep track of the lifecycle of the composable, and will ensure that the ViewModel is only active when the composable is visible.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In summary, the ViewModel in Jetpack Compose plays a crucial role in managing UI-related data, handling complex business logic, and ensuring a consistent and lifecycle-aware user experience. It facilitates a clean separation of concerns and promotes best practices in organizing and managing the app's architecture.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                    class PreviewTaskViewModel : ViewModel() {
                        private  val _tasks = mutableStateListOf(
                            Task(1, "Task 1", "Description 1"),
                            Task(2, "Task 2", "Description 2"),
                            Task(3, "Task 3", "Description 3"))

                        val tasks: List<Task> get() = _tasks

                        fun toggleTaskCompletion(task: Task) {
                            val index = _tasks.indexOf(task)
                            if (index != -1) {
                                _tasks[index] = task.copy(isCompleted = !task.isCompleted)
                            }
                        }

                        fun deleteTask(task: Task) {
                            _tasks.remove(task)
                        }
                    }
                    """.trimIndent()
                    )
                }
            }

            SpanText(
                text = "Can we use traditional Android Views and Compose together?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.XML_VS_COMPOSE,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "XML Vs Compose"
            )

            Ul {
                SpanText(
                    text = "Yes, we can use ComposeView to render Compose Ul within traditional Android views. We can also use AndroidView to render traditional views in our Compose Ul. (We can embed a traditional Android View inside a Jetpack Compose layout using the AndroidView composable. This allows to use any existing Android View component within a Compose UI). AndroidViewBinding for inflating view bindings inside Compose.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack Compose we often don't directly use the items of a stream but convert them into a State.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Because we still listen to streams that potentially can be triggered while the app is not present or remain over the app lifecycle without getting properly handled, we need to introduce a new toolkit of functions on how to handle these cases.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use the collectAsStateWithLifecycle API to collect flows in a lifecycle-aware manner from your UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    // Embedding a WebView inside a Jetpack Compose layout
                    @Composable
                    fun WebViewExample(url: String) {
                        AndroidView(
                            factory = { context ->
                                WebView(context).apply {
                                    settings.javaScriptEnabled = true
                                    loadUrl(url)
                                }
                            },
                            update = { webView ->
                                webView.loadUrl(url) // Allows updating the URL when it changes
                            }
                        )
                    }

                    // Add a ComposeView to your XML layout.
                    <androidx.compose.ui.platform.ComposeView
                        android:id="@+id/compose_view"
                        android:layout_width="match_parent"
                        android:layout_height="wrap_content" />

                    // In your Activity or Fragment, get a reference to ComposeView and set its content
                    val composeView = findViewById<ComposeView>(R.id.compose_view)
                    composeView.setContent {
                        Text("Hello from Jetpack Compose!")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Why Does Recomposition Happen (Occur)?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Change: When the state variable that a composable relies on changes, that composable is recomposed to reflect the new state. Whenever the state like ( remembe or MutableState ) used in the composable changes, recomposition is triggered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Side-effects change: If side-effect functions like ( LaunchedEffect, rememberUpdatedState , or SideEffect ) are used and the inputs change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Changes: When a composable with a key value detects that the key has changed, it can recompute that specific instance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Hoisting: if you are using this technique to facilitate sharing across multiple child components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Parameter Changes: If the parameters of a composable function change, it will also trigger recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Environment Changes: Changes in the environment (like themes or configurations) can cause recomposition as well.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A parent Composable triggers recomposition for its child Composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "New instances of Composables are created with different parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Compose decide which parts of the UI to recompose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose tracks state reads in each composable. When the state changes, only those composables reading the changed state are recomposed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lambda Reducing Recomposition Overhead",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When I was exploring the LinearProgressIndicator and CircularProgressIndicator APIs, I noticed that the versions with the progress: Float parameter have been deprecated. Instead, they have been replaced with a new lambda-based parameter: progress: () -> Float.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lambda ensures that Compose only reads the value when drawing the progress bar, rather than forcing recompositions every time progress changes. This is particularly useful when the progress value is derived from State, Flow, or LiveData.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Skips recomposition by reading during draw.
                    @Composable
                    fun LinearProgressIndicator(
                        progress: () -> Float, // Lambda returning Float
                        modifier: Modifier = Modifier,
                        color: Color = ProgressIndicatorDefaults. linearColor,
                        trackColor: Color = ProgressIndicatorDefaults.linearTrackColor,
                        strokeCap: StrokeCap = ProgressIndicatorDefaults.LinearStrokeCap,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Avoids recomposition for visual updates.
                    var stockPriceUpdate by remember & mutableFloatState0f(10f) }
                    val stableLambda = remember { { stockPriceUpdate }}

                    // Passing a stable lambda
                    DisplayNormalText(stockPrice = stableLambda)

                    Button(
                            onClick = { stockPriceUpdate += 5f },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Log. d ("LAMBDAOVERHEAD", "Recom Button" )
                            Text ("Increase")
                        }

                    //Not good way
                    @Composable
                    fun DisplayNormalText(stockPrice: () -> Float) {
                        Text ("Stock Price: ${stockPrice()}") //Recomposes on value change!
                    }

                    //Good way
                    @Composable
                    fun DisplayNormalText(stockPrice: () -> Float) {
                        // This log should appear only once at first composition.
                        Log. d ("LAMBDAOVERHEAD", "Recom Display")
                        val textMeasurer = rememberTextMeasurer (DEFAULT_BUFFER_SIZE)
                        Canvas (modifier = Modifier
                            •fillMaxWidth( )
                            •height (100. dp),
                            onDraw = {
                                drawText(
                                textMeasurer = textMeasurer,
                                text = stockPrice( ).toString(),
                                topLeft = Offset (10. dp.toPx), 10. dp. toPx())
                            })
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Explain why lambdas passed to composables can cause unnecessary recomposition, and how to avoid it.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unstable lambda captures (e.g., capturing a var or non-stable class) can be considered “unstable” by the compiler, forcing recomposition of the composable even when the actual invoked logic hasn’t meaningfully changed. Using stable/immutable data and hoisting/remembering lambdas (remember { { ... } } or the compiler’s automatic lambda memoization) mitigates this.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Which of the following will not trigger recomposition in a composable?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Updating a val state with remember { mutableStateOf(...) }",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Updating a val state with remember { mutableStateOf(...) }",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Correct: Changing a local variable inside onClick",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifying a state variable passed via a parameter",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Explanation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose triggers recomposition when it detects changes in observable state — such as mutableStateOf, StateFlow, or values tracked via remember.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A local variable inside a lambda, like one updated in onClick, is not observable. It doesn’t survive recomposition and isn’t tracked by Compose. So changing it won’t affect the UI.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Recompose other options",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember { mutableStateOf(...) }: This is observable state. Updating it will trigger recomposition.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Passing a lambda from a parent composable: If the lambda reference changes, Compose treats it as a parameter change and may recompose.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifying a state variable passed via a parameter: State passed to a composable is observed. If it changes, it will trigger recomposition.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Composition Vs. Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable functions may be recomposed as often as every frame (i.e., during animation). Also, developers should be clear that composable functions may be called in any order and that they may be executed in parallel. Therefore, we should avoid a huge logic that executes when a Composable function is executed. This is sometimes referred to as Side-Effects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition (Composable function calling itself) is triggered when a change in the state is made, so the UI is updated with this latest state. This is quite handy as only the composables with state change will be recomputed, but not the other composables. This will make the app more efficient and make the code easier for testing.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composition is the tree structure made up of composables used to describe the UI, accomplished by running composables. When we emit a user interface for the first time, that's the initial composition. During that initial composition, Jetpack Compose tracks the composables that we called to describe the user interface.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Does every State change cause an immediate Recomposition?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No. Recomposition is coordinated with the display’s refresh rate (frame coalescing). If you update a state 100 times between frames, Compose only recomposes once for the final value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why is remember necessary?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables are just functions. Without remember, your local variables would be re-initialized every time the function restarts. remember tells the \$composer to store and retrieve the value from the Slot Table.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SAM Single Abstract Method (Functional Interface)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An Interface with one abstract method that can be implemented using a lambda.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = $$"""
                        fun interface ClickListener {
                            fun onClick(id: Int)
                        }
                        
                        val listener = ClickListener { id ->
                            println("Clicked... $id")
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "High-Order Functions (HOF)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A function that takes another function as a parameter or returns a function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        fun execute(action: () -> Unit) {
                            action()
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inline possible",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "inline helps improve performance by eliminating lambda object creation. The compiler copies the function body directly to the call site, reducing memory overhead and extra stack calls - very useful in high-frequency Ul code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "reified (used only with inline functions) solves type erasure. It allows you to access the actual generic type at runtime, so you can use T::class or check value is T without passing a Class<T> manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No object creation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Faster for hot paths",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Function References (::) — Passing a Function as Data",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun isEven(x: Int) = x % 2 == 0
                    
                    val numbers = listOf(1, 2, 3, 4)
                    val evens = numbers.filter(::isEven)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class MyViewModel : ViewModel() {
                        fun onButtonClick() {
                            // handle click
                        }
                    }
                    
                    Button(
                        onClick = viewModel::onButtonClick
                    ) {
                        Text("Kotlin Multiplatform")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lambda-Friendly Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun myFilter(numbers: List<Int>, predicate: (Int) -> Boolean): List<Int> {
                        val result = mutableListOf<Int>()
                        for (n in numbers) {
                            if (predicate(n)) result.add(n)
                        }
                        return result
                    }
                    
                    val evens = myFilter(listOf(1,2,3,4)) { it % 2 == 0 }
                    """.trimIndent()
            )

            SpanText(
                text = "requireLazy",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    inline fun requireLazy(
                        value: Boolean,
                        lazyMessage: () -> Any
                    ) {
                        // FAST PATH
                        if (value) return

                        // SLOW PATH
                        val message = lazyMessage()
                        throw IllegalArgumentException(message.toString())
                    }
                    
                    fun expensiveMessage(): String {
                        println("Computing error message...")
                        return "Value must be positive"
                    }
                    
                    requireLazy(number > 0) {
                       "$number ${expensiveMessage()}"
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Nothing - (Function never returns normally)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Nothing represents code that never completes normally.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A function that always throws an exception.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A function that has an infinite loop.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A function after which no code is reachable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If a function returns Nothing, it tells the compiler that execution will not continue after that function call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    fun fail(message: String): Nothing {
                        throw IllegalStateException(message)  // always throws -- never returns
                    }


                    fun findUser(id: String): User {
                        return users[id] ?: fail("User $id not found")  // compiles -- Nothing fits here
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "callable reference",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Syedwaheed/CineVault.git
            KotlinCode(
                code = """
                    private const val TIMEOUT_MS = 30_000L

                    fun buildHttpClient(
                        baseUrl: String,
                        tokenProvider: TokenProvider,
                        logger: (String) -> Unit = ::println,
                    ): HttpClient = HttpClient(CIO) {
                        
                        install(HttpTimeout) {
                            requestTimeoutMillis = TIMEOUT_MS
                            connectTimeoutMillis = TIMEOUT_MS
                            socketTimeoutMillis = TIMEOUT_MS
                        }

                        install(ContentNegotiation) {
                            json(Json {
                                isLenient = true
                                ignoreUnknownKeys = true
                            })
                        }

                        install(Logging) {
                            this.logger = object : Logger {
                                override fun log(message: String) = logger(message)
                            }
                            level = LogLevel.BODY
                        }

                        install(AuthPlugin) {
                            this.tokenProvider = tokenProvider::getAccessToken
                        }

                        install(TokenRefreshPlugin) {
                            this.tokenProvider = tokenProvider::getAccessToken
                            this.onRefreshToken = tokenProvider::refreshAccessToken
                        }

                        defaultRequest {
                            url(baseUrl)
                            contentType(ContentType.Application.Json)
                        }

                    }.also { client ->
                        client.attributes.put(BaseUrlKey, baseUrl)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Unit() - High-Order Functions (HOF)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unit represents no meaningful return value. Unit is a function type and the Unit indicates that this function type does not return any meaningful value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If a function does not return anything meaningful, its return type is Unit.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, a function that only prints something and does not return a value returns Unit.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/odaridavid/Smoovie-KMP.git
            KotlinCode(
                code = """
                    private data class MovieActions(
                        val onSearchQueryChanged: (String) -> Unit = {},
                        val onFilterApplied: (Int?, String, Float) -> Unit = { _, _, _ -> },
                        val onRetry: () -> Unit = {},
                        val onLoadMore: () -> Unit = {},
                        val onMovieClick: (MovieUiModel) -> Unit = {},
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun logMessage(msg: String): Unit {  // same as writing nothing
                        println(msg)
                    }

                    val result = logMessage("hi")  // result is of type Unit
                    """.trimIndent()
            )

            //onQuantityChanged: https://github.com/Joker-x-dev/CoolMallKotlin.git
            KotlinCode(
                code = """
                    //1 
                    fun runBlock(
                        block: () -> Unit), //High-Order Functions (HOF)
                        onClick: (() -> Unit)?, //callbacks to composables
                        onClick = {},
                        onUserAvailable: (user: User?) -> Unit,
                        onQuantityChanged: (Long, Int) -> Unit = { _, _, ->}, //_ (Underscore): In Kotlin, an underscore is used for lambda parameters that are unused. Since this default implementation doesn't need to do anything with the ID or the quantity, both parameters are ignored.
                        
                        //Slot APIs (passing @Composable () -> Unit) are a powerful pattern for building flexible and decoupled UI layouts. They allow the top-level screen to define content for a leaf node without intermediate layers ever knowing the data requirements of that leaf.
                        content: @Composable () -> Unit, 
                        
                        leadingContent: @Composable RowScope.() -> Unit, //leadingContent()
                        extraContent: @Composable (() -> Unit)? = null // extraContent?.invoke()
                    ) {
                        block()
                    }

                    fun main() {
                        runBlock { println("Here") } // Prints: Here
                    }
                    
                    //2
                    @Composable
                    fun TitleWithAction(modifier: Modifier = Modifier,
                    action: (() -> Unit)? = null) {
                        //onClick
                        action?.invoke()
                    }
                    
                    //3
                    typealias OnConfirm = (String, Boolean, Int?, Int?) -> Unit
                    fun ConfirmScreen(
                        onConfirm: OnConfirm
                    )
                    
                    fun ConfirmScreen(
                        onConfirm: (String, Boolean, Int?, Int?) -> Unit
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //typealias Composable
                    
                    //typedef VoidBoolCallback = void Function(bool value); //Human readable
                    typealias ComposableFactory = @Composable () -> Unit

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun AppBar(
                        title: String = "",
                        navIcon: AppBarIconModel? = null,
                        containerColor: Color = MaterialTheme.colorScheme.primary,
                        titleContentColor: Color = MaterialTheme.colorScheme.onPrimary,
                        actions: @Composable RowScope.() -> Unit = {},
                    ) {
                        TopAppBar(
                            title = { Text(text = title) },
                            navigationIcon = if (navIcon == null) {
                                composableFactory { }
                            } else {
                                composableFactory { AppBarIcon(navIcon) }
                            },
                            colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                containerColor = containerColor,
                                titleContentColor = titleContentColor,
                                navigationIconContentColor = titleContentColor,
                                actionIconContentColor = titleContentColor,
                            ),
                            actions = actions
                        )
                    }

                    @Composable
                    internal fun AppBarIcon(navIcon: AppBarIconModel) {
                        IconButton(onClick = { navIcon.action() }) {
                            Icon(imageVector = navIcon.icon, contentDescription = navIcon.contentDescription)
                        }
                    }

                    fun composableFactory(create: @Composable () -> Unit): ComposableFactory {
                        val factory: @Composable () -> Unit = {
                            create()
                        }
                        return factory
                    }

                    data class AppBarIconModel(
                        val icon: ImageVector,
                        val contentDescription: String,
                        val action: () -> Unit,
                    )
                    """.trimIndent()
            )

            //https://github.com/KyNarec/SubSched.git
            KotlinCode(
                code = """
                    enum class TransitionEffect(val label: String) {
                        SlideVertical("Slide Vertical"),
                        SlideHorizontal("Slide Horizontal"),
                        Scale("Scale"),
                        Fade("Fade"),
                        Expand("Expand"),
                        None("None")
                    }
                    
                    @Composable
                    fun <T : Enum<T>> SettingComponentEnumChoice(
                        icon: ImageVector,
                        title: String,
                        description: String,
                        enumValues: List<T>,
                        selected: T,
                        onValueSelected: (T) -> Unit,
                        labelMapper: (T) -> String = { it.name }
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {  }
                                .padding(16.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(icon, contentDescription = title, modifier = Modifier.size(48.dp).padding(8.dp))
                                Spacer(Modifier.width(8.dp))
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(title, style = MaterialTheme.typography.titleMedium)
                                    Text(description, style = MaterialTheme.typography.bodyMedium)
                                    Text(labelMapper(selected), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }
                    }
                    
                     SettingComponentEnumChoice(
                        icon = Icons.Default.Animation,
                        title = "Screen Transition Effect",
                        description = "Choose how screens transition in the app",
                        enumValues = TransitionEffect.entries,
                        selected = transitionEffectFlow,
                        onValueSelected = {
                               
                        },
                        labelMapper = { it.label }
                     )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //swiftUI
                    struct InfoCard<Content: View>: View {
                        let title: String
                        let content: Content

                        init(title: String, @ViewBuilder content: () -> Content) {
                            self.title = title
                            self.content = content()
                        }
                        
                        var body: some View {
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    SearchBar(text: $searchText, onSearch: { query in
                           //call function
                        })
                    
                    struct SearchBar: View {
                        @Binding var text: String
                        var onSearch: (String) -> Void

                        var body: some View {
                            HStack {
                                Image(systemName: "magnifyingglass")
                                    .foregroundColor(.gray)

                                TextField(String(localized: "characters_search_placeholder"), text: $text)
                                    .textFieldStyle(PlainTextFieldStyle())
                                    .onChange(of: text) { newValue in
                                        onSearch(newValue)
                                    }

                                if !text.isEmpty {
                                    Button(
                                        action: {
                                            text = ""
                                            onSearch("")
                                        },
                                        label: {
                                            Image(systemName: "xmark.circle.fill")
                                                .foregroundColor(.gray)
                                        }
                                    )
                                }
                            }
                            .padding(12)
                            .background(Color.appSurface)
                            .cornerRadius(24)
                            .shadow(color: Color.black.opacity(0.1), radius: 4, x: 0, y: 2)
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, (T) -> Unit callbacks are the primary way state changes are communicated upward (State Hoisting). Even if we don't call it a \"Consumer,\" the pattern is the heartbeat of modern UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun FilterSwitch(onChanged: (Boolean) -> Unit) { 
                        // onChanged acts as the consumer of the Switch state
                        Switch(checked = true, onCheckedChange = onChanged) 
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dialog Open",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/DeFerence3/broke-compose.git
            KotlinCode(
                code = """
                    //1
                    @Composable
                    inline fun Boolean.OnShowDialog(dialog: @Composable () -> Unit) =
                    if (this) dialog.invoke() else Unit
                    
                    //2
                    @Composable
                    inline fun <T> T?.OnShowDialog(dialog: @Composable (T) -> Unit) =
                    if (this != null) dialog.invoke(this) else Unit
                    
                    //2
                    data class TransactionGroupState(
                        val isLoading: Boolean = false,
                        val selectedTransactionGroup: TransactionGroup? = null
                    )
                    
                    @Entity(tableName = "transaction_group")
                    data class TransactionGroup(
                        @PrimaryKey(autoGenerate = true)
                        @ColumnInfo(name = "id")
                        val id: Int,
                        @ColumnInfo(name = "category_name")
                        val name: String,
                        @ColumnInfo(name = "description")
                        val description: String?
                    )
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun TransactionGroupScreen(
                        onEvent: (TransactionGroupAction) -> Unit,
                        state: TransactionGroupState
                    ) {
                         state.selectedTransactionGroup.OnShowDialog {
                             BrokeDialog() {}
                         }
                    }
                    
                    @Composable
                    fun BrokeDialog(
                        modifier: Modifier = Modifier,
                        title: String,
                        onPositiveAction: () -> Unit,
                        positiveText: String,
                        negativeText: String,
                        onNegativeAction: () -> Unit,
                        positiveButtonEnabled: Boolean = true,
                        negativeButtonEnabled: Boolean = true,
                        content: @Composable (ColumnScope.() -> Unit)
                    ) { }
    
                    //1
                    var showDatetimepicker by remember { mutableStateOf(false) }
                    val datePickerState = rememberDatePickerState( initialSelectedDateMillis = selectedDate )
                    
                    showDatetimepicker.OnShowDialog
                    {
                        selectedDate = datePickerScreen(
                            datePickerState = datePickerState,
                            onShowDialogChange = { showDatetimepicker = it }
                        )
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun datePickerScreen(
                        datePickerState: DatePickerState,
                        onShowDialogChange: (Boolean) -> Unit
                    ): Long {
                        var selectedDate: Long = datePickerState.selectedDateMillis!!
                        DatePickerDialog(
                            onDismissRequest = { onShowDialogChange(false) },
                            confirmButton = {
                                TextButton(onClick = {
                                    onShowDialogChange(false)
                                    selectedDate = datePickerState.selectedDateMillis!!
                                }) {
                                    Text("Ok")
                                }
                            },
                            dismissButton = {
                                TextButton(onClick = { onShowDialogChange(false) }) {
                                    Text("Cancel")
                                }
                            }
                        ) {
                            DatePicker(state = datePickerState)
                        }
                        return selectedDate
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "invoke()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Only when you need to treat the function as an object and call it dynamically, but syntactically content(padding) is cleaner.Only when you need to treat the function as an object and call it dynamically, but syntactically content(padding) is cleaner.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Safer pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Tips Smart Work
            KotlinCode(
                code = """
                    //Compose uses referential equality to decide recomposition.
                    //New lambda = potential recomposition.
                    
                    //1
                    val onUserClick: (String) -> Unit = remember {
                        { id -> viewModel.onUserClick(id) }
                    }  // ❌ captures old VM

                    items(users) { user ->
                        UserItem(
                            user = user,
                            onClick = { onUserClick(user.id) }
                        )
                    }
                    
                    //2. Correct pattern if viewModel may change
                    val latestVm by rememberUpdatedState(viewModel)

                    val onUserClick: (String) -> Unit = remember {
                        { id -> latestVm.onUserClick(id) }
                    }
                    
                    //3.
                    val onUserClick: (String) -> Unit = viewModel::onUserClick
                    """.trimIndent()
            )

            //https://github.com/TinaT2/TimerzeerKMP.git
            KotlinCode(
                code = """
                    @Composable
                    fun ColorfulScaffold(
                        content: @Composable (PaddingValues) -> Unit
                    ) { 
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .clickable {
                                   
                                }) {

                            backgrounds()[customGraphicIds.backgroundId]?.invoke()

                            Scaffold(
                                modifier = modifier
                                    .background(customColors.mainBackground)
                                    .padding(top = SizeXL)
                            ) { paddingValues ->
                                content.invoke(paddingValues)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lambda Memoization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is commonly used when a Composable expects a lambda, and you want to pass a ViewModel function directly without wrapping it in another lambda.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Deferring reads only works if the lambdas you pass are stable. Lambdas recreated on each recomposition produce new instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When passed as parameters, this can prevent composables from being skipped because instance equality (===) fails.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Fix: Use Method References or remember your lambdas. For example, if you are passing action lambdas down your tree:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // ❌ Unstable: Creates a new lambda instance every recomposition
                    ActionButtons(
                        onPositive = { viewModel.submit() }, 
                        onNegative = { viewModel.cancel() }
                    )

                    // ✅ Stable: Uses method references (or remember { ... })
                    ActionButtons(
                        onPositive = viewModel::submit,
                        onNegative = viewModel::cancel
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Modifier.() -> Modifier",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    inline fun Modifier.applyIf(condition : Boolean, modifier : Modifier.() -> Modifier) : Modifier {
                        return if (condition) {
                            then(modifier(Modifier))
                        } else {
                            this
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}


